import { createSignal, onCleanup } from "solid-js";
import { fetchSource } from "./fetch-source";
import { publicSource } from "./public-source";
import { applyPatch, type EventState } from "./event-store";
import type { SourceMode } from "./types";

interface UseEventsOpts {
    mode: SourceMode;
    token?: string;
    onTokenRevoked?: () => void;
}

interface StateSnapshot {
    courts: Array<{ id: string; [key: string]: unknown }>;
    queue: Array<unknown>;
    settings: import("@saquei/types").TenantSettings | null;
}

const lsKey = (tenant: string): string => `saquei.sse.lastEventId.${tenant}`;

export function useEvents(tenant: string, opts: UseEventsOpts) {
    const initial: EventState = {
        courts: {},
        queue: [],
        settings: null,
        connected: false,
        lastEventId: localStorage.getItem(lsKey(tenant)),
        recentEvents: [],
    };
    const [state, setState] = createSignal<EventState>(initial);

    const fetchInitial = async () => {
        const headers: HeadersInit = opts.token
            ? { authorization: `Bearer ${opts.token}` }
            : {};
        const r = await fetch("/api/v1/state", { headers });
        if (!r.ok) return;
        const snap = (await r.json()) as StateSnapshot;
        setState((s) => ({
            ...s,
            courts: Object.fromEntries(
                snap.courts.map((c) => [c.id, c as import("@saquei/types").CourtSummary]),
            ),
            queue: snap.queue as import("@saquei/types").QueueListItem[],
            settings: snap.settings,
        }));
    };

    void fetchInitial();

    const url = "/api/v1/events";
    const source =
        opts.mode === "authenticated" && opts.token
            ? fetchSource({ url, token: opts.token, lastEventId: state().lastEventId })
            : publicSource(url);

    source.onEvent((e) => {
        if (e.event_type === "system.token_revoked") {
            opts.onTokenRevoked?.();
            return;
        }
        if (
            e.event_type === "system.lagged" ||
            e.event_type === "system.replay_truncated"
        ) {
            void fetchInitial();
            return;
        }
        setState((s) => {
            const next = applyPatch(s, e);
            if (next._needsRefetch) {
                void fetchInitial();
                const settled = { ...next, _needsRefetch: false };
                if (opts.mode === "authenticated") {
                    localStorage.setItem(lsKey(tenant), settled.lastEventId ?? "");
                }
                return settled;
            }
            if (next._settingsChanged) {
                void fetchInitial();
                const settled = { ...next, _settingsChanged: false };
                if (opts.mode === "authenticated") {
                    localStorage.setItem(lsKey(tenant), settled.lastEventId ?? "");
                }
                return settled;
            }
            if (opts.mode === "authenticated") {
                localStorage.setItem(lsKey(tenant), next.lastEventId ?? "");
            }
            return next;
        });
    });

    onCleanup(() => source.close());

    return state;
}
